import { useCallback, useEffect, useRef, useState } from 'react';
import {
  STORAGE_KEYS,
  readStorageItem,
  removeStorageItem,
  writeStorageItem,
} from '../utils/storage';
import maintainerMetrics from '../services/maintainerMetrics';
import {
  isRecoveryCandidateRoute,
  isWelcomeRoute,
} from '../state/routeState';

export const useAppBootstrap = ({
  router,
  speedInsightsClient,
  showNotificationsPanel,
  setShowNotificationsPanel,
  listSavedForms,
  formsRevision,
  userId,
}) => {
  const [recoveryForms, setRecoveryForms] = useState([]);
  const [currentPath, setCurrentPath] = useState(() => {
    if (typeof window !== 'undefined') return window.location.pathname;
    return null;
  });
  const [hasShownRecovery, setHasShownRecovery] = useState(() => {
    if (typeof window === 'undefined') return false;
    return readStorageItem(STORAGE_KEYS.RECOVERY_BANNER_SHOWN, { area: 'session' }) === '1';
  });
  const [showRecoveryBanner, setShowRecoveryBanner] = useState(false);
  const hadRecoveryFormsRef = useRef(false);
  const normalizeUserId = useCallback((value) => {
    if (!value) return '';
    return String(value).trim();
  }, []);

  const isOwnedRecoveryForm = useCallback((entry) => {
    const activeUserId = normalizeUserId(userId);
    if (!activeUserId) return false;

    const ownerUserId = normalizeUserId(
      entry?.meta?.userId
      || entry?.meta?.ownerId
      || entry?.meta?.createdBy,
    );

    if (!ownerUserId) return false;
    return ownerUserId === activeUserId;
  }, [normalizeUserId, userId]);

  const markRecoveryBannerShown = useCallback(() => {
    setHasShownRecovery(true);
    if (typeof window !== 'undefined') {
      writeStorageItem(STORAGE_KEYS.RECOVERY_BANNER_SHOWN, '1', { area: 'session' });
    }
  }, []);

  const resetRecoveryBannerShown = useCallback(() => {
    setHasShownRecovery(false);
    if (typeof window !== 'undefined') {
      removeStorageItem(STORAGE_KEYS.RECOVERY_BANNER_SHOWN, { area: 'session' });
    }
  }, []);

  const handleClickOutside = useCallback((event) => {
    if (!showNotificationsPanel) return;

    const target = event.target;
    if (target?.closest && target.closest('.notifications-panel')) return;
    if (target?.closest && target.closest('.user-controls__notification-button')) return;

    setShowNotificationsPanel(false);
  }, [setShowNotificationsPanel, showNotificationsPanel]);

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [handleClickOutside]);

  useEffect(() => {
    const saved = listSavedForms();
    const ownedSaved = saved.filter(isOwnedRecoveryForm);
    const hasOwnedUser = normalizeUserId(userId);

    if (!hasOwnedUser || !ownedSaved.length) {
      setRecoveryForms([]);
      setShowRecoveryBanner(false);
      hadRecoveryFormsRef.current = false;
      return;
    }

    if (!hadRecoveryFormsRef.current) {
      resetRecoveryBannerShown();
    }
    hadRecoveryFormsRef.current = true;
    setRecoveryForms(ownedSaved);
  }, [formsRevision, isOwnedRecoveryForm, listSavedForms, normalizeUserId, resetRecoveryBannerShown, userId]);

  useEffect(() => {
    if (!speedInsightsClient) return;

    if (typeof router?.subscribe !== 'function') {
      speedInsightsClient.setRoute(
        typeof window !== 'undefined' ? window.location.pathname : null,
      );
      return undefined;
    }

    speedInsightsClient.setRoute(
      router.state?.location?.pathname ?? (typeof window !== 'undefined' ? window.location.pathname : null),
    );

    const unsubscribe = router.subscribe((state) => {
      const nextRoute = state.location?.pathname ?? null;
      speedInsightsClient.setRoute(nextRoute);
    });

    return unsubscribe;
  }, [router, speedInsightsClient]);

  useEffect(() => {
    if (typeof router?.subscribe !== 'function') {
      if (typeof window !== 'undefined') {
        const pathname = window.location.pathname;
        setCurrentPath(pathname);
        maintainerMetrics.setActiveMetricsRoute(pathname);
      }
      return undefined;
    }

    const initialPath = router.state?.location?.pathname ?? (typeof window !== 'undefined' ? window.location.pathname : null);
    setCurrentPath(initialPath);
    maintainerMetrics.setActiveMetricsRoute(initialPath);
    const unsubscribe = router.subscribe((state) => {
      const nextPath = state.location?.pathname ?? null;
      setCurrentPath(nextPath);
      maintainerMetrics.setActiveMetricsRoute(nextPath);
    });
    return unsubscribe;
  }, [router]);

  useEffect(() => {
    if (!recoveryForms.length) {
      setShowRecoveryBanner(false);
      return;
    }

    if (isRecoveryCandidateRoute(currentPath)) {
      if (isWelcomeRoute(currentPath)) {
        setShowRecoveryBanner(false);
        return;
      }
      if (!hasShownRecovery) {
        setShowRecoveryBanner(true);
        markRecoveryBannerShown();
      }
      return;
    }

    setShowRecoveryBanner(false);
  }, [currentPath, hasShownRecovery, markRecoveryBannerShown, recoveryForms.length]);

  const hideRecoveryBanner = useCallback(() => {
    setShowRecoveryBanner(false);
  }, []);

  return {
    recoveryForms,
    setRecoveryForms,
    showRecoveryBanner,
    hideRecoveryBanner,
    resetRecoveryBannerShown,
  };
};
